// The closing drawer: work the documentation checklist, confirm premium,
// close the file, and raise the technical account it settles against.
//
// That last one is the link the chain was missing. A bound facultative risk
// had nowhere to go: the closing existed, the money had nowhere to be
// recorded, and the workflow stopped.

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  C, Pill, Panel, Row, NextAction, money0, date,
} from './kit';
import {
  useAction, Notice, ActionButton, ActionRow, ConfirmAction, ToggleChecklist,
  EditPanel, StatusAction,
} from './actions';
import { ActivityTab } from './tabs';

// Premium confirmed by the market: three states, because "nobody has asked"
// and "asked and refused" are different facts.
function ConfirmedPill({ value }) {
  if (value === null || value === undefined) {
    return <span style={{ color: C.faint, fontSize: 12.5 }}>Not yet asked</span>;
  }
  const yes = value === true;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 11px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 700,
      background: yes ? C.greenBg : C.redBg, color: yes ? C.green : C.red,
    }}>
      {yes ? 'Yes' : 'No'}
    </span>
  );
}

export default function ClosingDrawer({ row: r, tab, reload }) {
  const history = useHistory();
  const act = useAction(reload);
  const [list, setList] = useState(r.checklist || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setList(r.checklist || []); }, [r._id]);

  const closed = r.status === 'closed';
  const done = list.filter(i => i.completed).length;

  // Ticking an item saves immediately. A checklist with a Save button is a
  // checklist people forget to save, and a half-recorded closing file is
  // worse than an untouched one.
  const toggle = (i, next) => {
    const updated = list.map((item, idx) => (idx === i
      ? { ...item, completed: next, completedAt: next ? new Date().toISOString() : undefined }
      : item));
    setList(updated);
    return act.run({
      method: 'patch',
      url: `/reinsurance/closings/${r._id}`,
      body: { closingChecklist: updated },
      success: null,
    });
  };

  if (tab === 'Documentation') {
    return (
      <Panel title='Documentation checklist'>
        <Notice error={act.error} note={act.note} />
        <ToggleChecklist
          items={list}
          onToggle={toggle}
          saving={act.saving}
          countLabel={`${done} of ${list.length} completed`}
        />
      </Panel>
    );
  }

  if (tab === 'Financials') {
    return (
      <>
        <Notice error={act.error} note={act.note} />
        <Panel title='Financials'>
          <Row label='Signed share' value={r.signedShare ? `${r.signedShare}%` : null} />
          <Row label='Signed lines' value={money0(r.signedAmount, r.currency)} />
          <Row label='Premium (est.)' value={r.premiumEstimate ? money0(r.premiumEstimate, r.currency) : null} />
          <Row label='Premium confirmed' value={<ConfirmedPill value={r.premiumConfirmed} />} />
          <Row label='Currency' value={r.currency} />
        </Panel>
        <Panel title='Premium confirmation'>
          <ActionRow>
            <ActionButton
              busy={act.saving}
              onClick={() => act.run({
                method: 'patch',
                url: `/reinsurance/closings/${r._id}`,
                body: { premiumConfirmed: true },
                success: 'Premium confirmed with the market.',
              })}
            >
              Confirm premium
            </ActionButton>
            <ActionButton
              tone='quiet'
              busy={act.saving}
              onClick={() => act.run({
                method: 'patch',
                url: `/reinsurance/closings/${r._id}`,
                body: { premiumConfirmed: false },
                success: 'Recorded as not confirmed.',
              })}
            >
              Mark unconfirmed
            </ActionButton>
          </ActionRow>
        </Panel>
      </>
    );
  }

  // ─── Activity ───
  // `dealId`, not `_id`. A closing row's id is the compound
  // `<dealId>:<marketId>` — one deal unwinds into one row per market — and
  // the deal is what the audit trail is actually recorded against.
  if (tab === 'Activity') return <ActivityTab resource='closings' id={r.dealId} />;


  // ─── Overview ───
  return (
    <>
      <Notice error={act.error} note={act.note} />

      <Panel title='Key information'>
        <Row label='Closing ref' value={r.reference} />
        <Row label='Cedant' value={r.cedantName} />
        <Row label='Counterparty' value={r.counterpartyName} />
        <Row label='Section' value={r.section} />
        <Row label='Inception date' value={date(r.inceptionDate)} />
        <Row label='Signed lines' value={money0(r.signedAmount, r.currency)} />
        <Row label='Premium (est.)' value={r.premiumEstimate ? money0(r.premiumEstimate, r.currency) : null} />
        <Row label='Premium confirmed' value={<ConfirmedPill value={r.premiumConfirmed} />} />
        <Row label='Date closed' value={date(r.closedAt)} />
        <Row label='Handled by' value={r.handledByName} />
        <Row label='Status' value={<Pill status={r.status} size='sm' />} />
      </Panel>

      {/* ── The record itself ──
          QA found this screen had no edit, and a status control that offered
          only "Mark as closed" — so a closing could never go back to pending
          docs, and a wrong inception date stayed wrong. */}
      <Panel title='Record'>
        <div style={{ display: 'grid', gap: 10 }}>
          <EditPanel
            record={r}
            endpoint='/reinsurance/closings'
            onSaved={reload}
            fields={[
              { key: 'premiumEstimate', label: 'Premium (est.)', type: 'number' },
              { key: 'signedAmount', label: 'Signed lines', type: 'number' },
              { key: 'handledByName', label: 'Handled by' },
              { key: 'closedAt', label: 'Date closed', type: 'date' },
            ]}
          />
          {/* A closing is a market line on a deal, so its state lives in
              `closingStatus` and only the fields in CLOSING_WRITABLE can be
              set. There is deliberately no void/cancel here: removing a
              closing means taking the market off the deal, which is the
              placement screen's job, not a record deletion. */}
          <StatusAction
            record={r}
            endpoint='/reinsurance/closings'
            field='closingStatus'
            onSaved={reload}
            options={[
              { value: 'in_progress', label: 'In Progress' },
              { value: 'awaiting_signature', label: 'Awaiting Signature' },
              { value: 'pending_docs', label: 'Pending Docs' },
              { value: 'closed', label: 'Closed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
          />
        </div>
      </Panel>

      <Panel title='Documentation checklist'>
        <ToggleChecklist
          items={list}
          onToggle={toggle}
          saving={act.saving}
          countLabel={`${done} of ${list.length} completed`}
        />
      </Panel>

      <Panel title='Documents'>
        <ActionRow>
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              // No market id: the debit note is a whole-deal document,
              // unlike the credit note which is raised per market.
              url: `/reinsurance/deals/${r.dealId}/closings/debit-note`,
              success: 'Debit note generated.',
            })}
          >
            Issue debit note
          </ActionButton>
          <ActionButton
            tone='quiet'
            busy={act.saving}
            onClick={() => act.run({
              url: `/reinsurance/deals/${r.dealId}/closings/credit-note`,
              // The market's own id, not its name and not a position in an
              // array: a closing row is keyed <dealId>:<marketId>, so this is
              // the one handle that cannot address the wrong reinsurer.
              body: { reinsurerId: r.marketId },
              success: 'Credit note generated.',
            })}
          >
            Issue credit note
          </ActionButton>
        </ActionRow>
      </Panel>

      {/* ── The link to the money ──
          Raising the account is what turns a closed file into something the
          accounting screen can settle. Idempotent server-side, so pressing it
          twice returns the account that already exists. */}
      <Panel title='Settlement'>
        <ActionRow>
          <ActionButton
            busy={act.saving}
            onClick={async () => {
              const account = await act.run({
                url: '/reinsurance/accounts/from-closing',
                body: { closing: r._id },
                // The endpoint is idempotent: a closing that already has an
                // account returns it with `created: 0` rather than raising a
                // second one. Saying "raised" either way told the user a
                // number had been spent when it had not.
                success: (d) => (d?.alreadyExisted
                  ? 'That closing already has a technical account — opening Bordereaux & Accounting.'
                  : 'Technical account raised — settle it under Bordereaux & Accounting.'),
              });
              if (account) history.push('/reinsurance/accounting');
            }}
          >
            Raise technical account
          </ActionButton>
          {!closed && (
            <ConfirmAction
              label='Mark as closed'
              question={`Close ${r.reference}? This stamps the closing date and confirms the premium.`}
              confirmLabel='Close file'
              busy={act.saving}
              onConfirm={() => act.run({
                method: 'patch',
                url: `/reinsurance/closings/${r._id}`,
                body: { closingStatus: 'closed' },
                success: 'Closing completed.',
              })}
            />
          )}
        </ActionRow>
      </Panel>

      {closed ? (
        <NextAction
          tone='green'
          label='Closing completed'
          detail='All documentation received. File archived.'
        />
      ) : (
        <NextAction
          label='Complete the closing file'
          detail='Collect the outstanding documents and confirm premium with the market.'
        />
      )}
    </>
  );
}
